/**
 * Physically based single-scattering atmosphere (Rayleigh + Mie + ozone),
 * authored in photometric units: the integral is driven by the extraterrestrial
 * solar illuminance (128 000 lx) so the result is sky luminance in cd/m2 (nits).
 *
 * That is what makes the tonemapper behave filmically: a sunlit 18 % surface
 * lands around 3 000-4 000 nits, an open-sky-lit shadow around 300-600 nits,
 * and the exposure that maps those to a good frame is a real photographic EV.
 *
 * Transmittance to the sun is a CPU-baked LUT over (altitude, sun cosine).
 * Marching it per sample is what most naive implementations get wrong: a
 * uniformly stepped 250 km slant path resolves less than 15 % of the Mie
 * optical depth, which is exactly why so many procedural skies refuse to go
 * warm at low sun angles.
 *
 * Owner: lighting.
 */

/** Shared physical constants, mirrored in JS in Sky.js — keep the two in sync. */
export const ATMO = {
  Rg: 6360000.0,          // planet radius (m)
  Rt: 6460000.0,          // top of atmosphere (m)
  Hr: 8000.0,             // Rayleigh scale height
  Hm: 1200.0,             // Mie scale height
  betaR: [5.802e-6, 13.558e-6, 33.1e-6],   // Rayleigh scattering (Bruneton)
  betaM: 3.996e-6,        // Mie scattering at turbidity 1
  betaMext: 4.44e-6,      // Mie extinction at turbidity 1
  betaO: [0.650e-6, 1.881e-6, 0.085e-6],   // ozone absorption
  ozoneCenter: 25000.0,
  ozoneWidth: 15000.0,
  sunIlluminanceTOA: 128000.0, // lx, perpendicular, above the atmosphere
  sunAngularRadius: 0.004675,  // rad (0.267 deg)

  // transmittance LUT
  lutWidth: 256,
  lutHeight: 32,
  lutMaxAltitude: 60000.0,
  lutMuMin: -0.25,
  lutMuRange: 1.25,
};

export const ATMOSPHERE_GLSL = /* glsl */ `
#ifndef AAA_ATMOSPHERE_INCLUDED
#define AAA_ATMOSPHERE_INCLUDED

#define AAA_PI 3.141592653589793

const float AAA_Rg = ${ATMO.Rg.toFixed(1)};
const float AAA_Rt = ${ATMO.Rt.toFixed(1)};
const float AAA_Hr = ${ATMO.Hr.toFixed(1)};
const float AAA_Hm = ${ATMO.Hm.toFixed(1)};
const vec3  AAA_betaR = vec3( ${ATMO.betaR.map((v) => v.toExponential(6)).join(', ')} );
const float AAA_betaM = ${ATMO.betaM.toExponential(6)};
const float AAA_betaMe = ${ATMO.betaMext.toExponential(6)};
const vec3  AAA_betaO = vec3( ${ATMO.betaO.map((v) => v.toExponential(6)).join(', ')} );
const float AAA_SUN_TOA = ${ATMO.sunIlluminanceTOA.toFixed(1)};
const float AAA_LUT_MAXALT = ${ATMO.lutMaxAltitude.toFixed(1)};
const float AAA_LUT_MUMIN = ${ATMO.lutMuMin.toFixed(4)};
const float AAA_LUT_MURANGE = ${ATMO.lutMuRange.toFixed(4)};

uniform float uTurbidity;      // aerosol multiplier, 1 = pristine, 4 = hazy city
uniform float uOzone;          // ozone multiplier
uniform vec3  uGroundAlbedo;
uniform vec3  uSunDirection;   // world space, normalised, +Y up
uniform float uMieG;
uniform sampler2D uTransmittanceLUT;

/** Nearest positive root of |o + t d| = r, or -1. */
float aaaRaySphereNear( vec3 o, vec3 d, float r ) {
  float b = dot( o, d );
  float c = dot( o, o ) - r * r;
  float disc = b * b - c;
  if ( disc < 0.0 ) return -1.0;
  disc = sqrt( disc );
  float t0 = -b - disc;
  float t1 = -b + disc;
  if ( t1 < 0.0 ) return -1.0;
  return t0 < 0.0 ? t1 : t0;
}

/** Far root — distance to the top of the atmosphere from inside it. */
float aaaRaySphereFar( vec3 o, vec3 d, float r ) {
  float b = dot( o, d );
  float c = dot( o, o ) - r * r;
  float disc = b * b - c;
  if ( disc < 0.0 ) return 0.0;
  return max( 0.0, -b + sqrt( disc ) );
}

vec3 aaaDensities( float h ) {
  float o = max( 0.0, 1.0 - abs( h - ${ATMO.ozoneCenter.toFixed(1)} ) / ${ATMO.ozoneWidth.toFixed(1)} );
  return vec3( exp( -h / AAA_Hr ), exp( -h / AAA_Hm ), o );
}

vec3 aaaExtinction( vec3 opticalDepth ) {
  return exp( -( AAA_betaR * opticalDepth.x
               + AAA_betaMe * uTurbidity * opticalDepth.y
               + AAA_betaO * uOzone * opticalDepth.z ) );
}

/** Transmittance from altitude h toward a direction whose vertical cosine is mu. */
vec3 aaaTransmittance( float h, float mu ) {
  float v = sqrt( clamp( h / AAA_LUT_MAXALT, 0.0, 1.0 ) );
  float u = clamp( ( mu - AAA_LUT_MUMIN ) / AAA_LUT_MURANGE, 0.0, 1.0 );
  return texture2D( uTransmittanceLUT, vec2( u, v ) ).rgb;
}

vec3 aaaSunTransmittance( float altitude, vec3 sunDir ) {
  return aaaTransmittance( altitude, sunDir.y );
}

float aaaPhaseRayleigh( float mu ) {
  return ( 3.0 / ( 16.0 * AAA_PI ) ) * ( 1.0 + mu * mu );
}

float aaaPhaseHG( float mu, float g ) {
  float g2 = g * g;
  float d = 1.0 + g2 - 2.0 * g * mu;
  return ( 1.0 - g2 ) / ( 4.0 * AAA_PI * max( 1e-4, d * sqrt( max( 1e-4, d ) ) ) );
}

/** Cornette-Shanks — better forward lobe shape than plain HG for aerosols. */
float aaaPhaseMie( float mu, float g ) {
  float g2 = g * g;
  float num = 3.0 * ( 1.0 - g2 ) * ( 1.0 + mu * mu );
  float den = 8.0 * AAA_PI * ( 2.0 + g2 ) * pow( max( 1e-4, 1.0 + g2 - 2.0 * g * mu ), 1.5 );
  return num / den;
}

/**
 * Sky radiance (nits) looking along rayDir from altitude metres.
 * Steps are warped toward the camera (t ~ u^2.5) because the scattering medium
 * is exponential — uniform steps waste every sample in the vacuum up top.
 */
vec3 aaaSkyRadiance( vec3 rayDir, vec3 sunDir, float altitude, out vec3 viewTransmittance ) {
  vec3 p0 = vec3( 0.0, AAA_Rg + altitude, 0.0 );

  float tTop = aaaRaySphereFar( p0, rayDir, AAA_Rt );
  float tGround = aaaRaySphereNear( p0, rayDir, AAA_Rg );
  bool hitsGround = tGround > 0.0;
  float tMax = hitsGround ? tGround : tTop;

  const int N = 24;
  const float WARP = 2.5;

  vec3 od = vec3( 0.0 );
  vec3 sumR = vec3( 0.0 );
  vec3 sumM = vec3( 0.0 );

  float prevT = 0.0;
  for ( int i = 0; i < N; i ++ ) {
    float ub = float( i + 1 ) / float( N );
    float tb = tMax * pow( ub, WARP );
    float ds = tb - prevT;
    float tm = 0.5 * ( prevT + tb );
    prevT = tb;
    if ( ds <= 0.0 ) continue;

    vec3 s = p0 + rayDir * tm;
    float len = length( s );
    float h = max( 0.0, len - AAA_Rg );
    vec3 dens = aaaDensities( h );
    od += dens * ds;

    vec3 tView = aaaExtinction( od );
    vec3 tSun = aaaTransmittance( h, dot( s / len, sunDir ) );

    vec3 w = tView * tSun * ds;
    sumR += w * dens.x;
    sumM += w * dens.y;
  }

  float mu = dot( rayDir, sunDir );
  vec3 L = AAA_SUN_TOA * ( AAA_betaR * sumR * aaaPhaseRayleigh( mu )
                         + AAA_betaM * uTurbidity * sumM * aaaPhaseMie( mu, uMieG ) );

  // Cheap second-order term: the sky is never as dark as pure single scattering.
  L += AAA_SUN_TOA * AAA_betaR * sumR * 0.34 / ( 4.0 * AAA_PI );

  viewTransmittance = aaaExtinction( od );

  if ( hitsGround ) {
    vec3 hit = p0 + rayDir * tGround;
    float sunCos = max( 0.0, dot( normalize( hit ), sunDir ) );
    vec3 sunAtGround = AAA_SUN_TOA * aaaSunTransmittance( 0.0, sunDir );
    vec3 ground = uGroundAlbedo / AAA_PI * ( sunAtGround * sunCos + vec3( 1600.0, 2100.0, 3000.0 ) * max( 0.05, sunDir.y ) );
    L += viewTransmittance * ground;
  }

  return L;
}

#endif
`;
